import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { partnerThemes } from "../config/themeConfig";
import { getPartnerConfig } from "@/lib/apis";

const ThemeContext = createContext();

// Every CirclePe partner uses a light theme, so DB-driven partners (and the
// pre-resolve default) get a neutral light base — white background, dark text.
// These exact Tailwind classes already appear in themeConfig.js, so they're
// generated at build time and safe to apply at runtime.
const LIGHT_BASE = {
  bgColor: "#ffffff",
  backgroundColor: "bg-[#ffffff]",
  // Floating input labels "cut" the border with this bg on focus; keep it light.
  peerFocusColor: "peer-focus:bg-[#ffffff]",
  textColor: "text-[#666666]",
  // Readable dark heading when the partner has no brand colour set. When a brand
  // colour exists it's overridden below with the cp-brand-text class.
  headingColor: "text-[#222]",
  subHeadingColor: "text-[#555555]",
  darkTextColor: "text-[#555555]",
  primaryButtonTextColor: "text-[#ffffff]",
};

export const ThemeProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const { userIdentifier } = useParams();
  const location = useLocation();
  const partner = location?.pathname?.startsWith("/p")
    ? userIdentifier
    : searchParams.get("partner");
  const [theme, setTheme] = useState(partnerThemes.default || LIGHT_BASE);

  console.log(partner, "partner");

  useEffect(() => {
    document.body.style.backgroundColor = theme?.bgColor || "#ffffff";
  }, [theme]);

  useEffect(() => {
    if (!partner) return;
    // Hardcoded journey partners keep their rich theme.
    if (partnerThemes[partner]) {
      setTheme(partnerThemes[partner]);
      return;
    }
    // DB-driven partner (created from the B2B dashboard): pull its branding from
    // the backend so the logo — and, later, colors — come from the DB with no
    // hardcoding. `logoUrl` is rendered directly by Header.
    let active = true;
    getPartnerConfig(partner).then((cfg) => {
      if (!active || !cfg?.exists) return;
      // Drive the brand colour through a CSS variable + static utility classes
      // (cp-brand-text / cp-brand-bg). Because every journey screen already reads
      // theme.headingColor / theme.primaryButtonBgColor, pointing those at these
      // classes colours headings AND buttons across the whole journey with no
      // per-screen edits. The *_Code values stay as inline fallbacks.
      if (cfg.primaryColor) {
        document.documentElement.style.setProperty(
          "--cp-brand-color",
          cfg.primaryColor,
        );
      }
      setTheme((prev) => ({
        ...prev,
        ...LIGHT_BASE,
        name: cfg.tag,
        logoUrl: cfg.logo_url || "",
        ...(cfg.primaryColor
          ? {
              headingColor: "cp-brand-text",
              primaryButtonBgColor: "cp-brand-bg",
              primaryButtonBgColorCode: cfg.primaryColor,
              headingColorCode: cfg.primaryColor,
            }
          : {}),
      }));
    });
    return () => {
      active = false;
    };
  }, [partner]);

  return (
    <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  return useContext(ThemeContext);
};
